/**
 * Hospital Staff Financial ROI Module.
 * Adapted from Brain Health financialStaff.js for healthcare environments.
 *
 * 8 Channels:
 *   1. Burnout: Absenteeism   — payroll × 4.3% baseline × composite improvement
 *   2. Burnout: Presenteeism  — payroll × 4.3% × 3.5 multiplier × composite improvement
 *   3. Clinical Accuracy      — rework cost × (daylight + air quality + enrichment)
 *   4. Efficiency             — key work cost × (movement + wayfinding)
 *   5. Medication Errors      — admissions × 6.5% × $6K × 15% env share × composite
 *   6. Patient Falls          — admissions × 3.5% × $14K × 39% env share × composite
 *   7. Nurse Retention        — turnover cost × (nature + noise + daylight) composite
 *   8. Reputational Impact    — revenue × 2% VBP withhold × 25% HCAHPS weight × env composite
 *
 * Hospital-specific role defaults, evidence citations, and effect sizes.
 */

// ── Per-pathway effect sizes from published research ──
export const HC_PATHWAY_COEFFICIENTS = {
  daylight:       { channel: 'clinical_accuracy', effect: 0.03, study: 'Boubekri 2020', desc: '+3% cognitive performance from daylight access' },
  noise:          { channel: 'burnout',           effect: 0.07, study: 'Morrison 2003; Ryherd 2008', desc: '-7% burnout from noise control (hospital alarms avg 72dB in ICU)' },
  air_quality:    { channel: 'clinical_accuracy', effect: 0.08, study: 'Allen et al. 2015 COGfx', desc: '+8% cognitive function with better air quality' },
  nature:         { channel: 'retention',         effect: 0.06, study: 'Bjornstad 2016', desc: '-6% absenteeism from nature access' },
  enrichment:     { channel: 'clinical_accuracy', effect: 0.04, study: 'Nieuwenhuis 2014 (conservative for clinical)', desc: '+4% accuracy in enriched environments' },
  movement:       { channel: 'efficiency',        effect: 0.02, study: 'Hendrich 2008; Shepley 2012', desc: '+2% efficiency from better unit layout' },
  wayfinding:     { channel: 'efficiency',        effect: 0.03, study: 'Jamshidi 2025; Huelat 2003', desc: '+3% FTE recovery from hospital wayfinding (higher than office due to urgency)' },
  visual_stress:  { channel: 'burnout',           effect: 0.02, study: 'Wilkins 1989', desc: '-2% headache/fatigue from visual comfort' },
};

// ── Nurse burnout parameters ──
export const NURSE_BURNOUT_PARAMS = {
  absenteeismRate: 0.043,        // BLS healthcare sector — highest of any sector
  presenteeismMultiplier: 3.5,   // Hemp 2004 — presenteeism costs 3.5x absenteeism
  burnoutPrevalence: 0.40,       // MBI high emotional exhaustion
};

// ── Medication error parameters ──
export const MED_ERROR_PARAMS = {
  baseRatePerAdmission: 0.065,   // 6.5 per 100 admissions
  costPerPreventableADE: 6000,   // $5K-$7K midpoint
  environmentalSharePct: 0.15,   // Conservative attribution (82% fatigue-linked, but only fraction is env)
};

// ── Reputational impact parameters (HCAHPS / VBP) ──
export const REPUTATIONAL_PARAMS = {
  vbpWithholdPct: 0.02,           // 2% Medicare withhold for VBP
  hcahpsWeightInVbp: 0.25,        // HCAHPS is 25% of total VBP score
  maxHcahpsGainPct: 0.02,         // Cap: max 2% of total revenue (conservative ceiling)
  revenuePerHcahpsPoint: 0.0088,  // 0.88% revenue per HCAHPS point (Deloitte/CMS)
  maxStarGain: 0.5,               // Max ~0.5 star improvement from environment alone
};

// ── Patient fall parameters ──
export const PATIENT_FALL_PARAMS = {
  fallRatePerAdmission: 0.035,   // 3.5 falls per 100 admissions (AHRQ)
  costPerFall: 14000,            // Weighted avg including injury severity mix ($6,669 avg + serious injury cases)
  environmentalSharePct: 0.39,   // 39% — Joint Commission root cause: physical environment
};

// ── Hospital staff role defaults ──
export const HC_ROLE_DEFAULTS = {
  AttendingPhysician: {
    label: 'Physician (Attending)',
    avgHourlyCost: 150,
    replacementCostMultiple: 2.5,
    annualTurnoverRate: 0.06,
    pctTimeRework: 0.02,
    pctTimeKeyWork: 0.75,
  },
  ResidentPhysician: {
    label: 'Physician (Resident)',
    avgHourlyCost: 35,
    replacementCostMultiple: 0.5,
    annualTurnoverRate: 0.33,
    pctTimeRework: 0.04,
    pctTimeKeyWork: 0.70,
  },
  RegisteredNurse: {
    label: 'Registered Nurse (RN)',
    avgHourlyCost: 45,
    replacementCostMultiple: 1.5,
    annualTurnoverRate: 0.22,
    pctTimeRework: 0.06,
    pctTimeKeyWork: 0.60,
  },
  CNA: {
    label: 'Certified Nursing Assistant',
    avgHourlyCost: 18,
    replacementCostMultiple: 0.5,
    annualTurnoverRate: 0.35,
    pctTimeRework: 0.05,
    pctTimeKeyWork: 0.55,
  },
  Pharmacist: {
    label: 'Pharmacist',
    avgHourlyCost: 65,
    replacementCostMultiple: 1.5,
    annualTurnoverRate: 0.10,
    pctTimeRework: 0.04,
    pctTimeKeyWork: 0.65,
  },
  RespiratoryTherapist: {
    label: 'Respiratory Therapist',
    avgHourlyCost: 35,
    replacementCostMultiple: 1.0,
    annualTurnoverRate: 0.15,
    pctTimeRework: 0.04,
    pctTimeKeyWork: 0.65,
  },
  Administrative: {
    label: 'Administrative / Support',
    avgHourlyCost: 22,
    replacementCostMultiple: 0.5,
    annualTurnoverRate: 0.20,
    pctTimeRework: 0.05,
    pctTimeKeyWork: 0.55,
  },
};

// ── Evidence Citations ──
export const HC_STAFF_EVIDENCE = {
  burnout_absenteeism: [
    { author: 'Bureau of Labor Statistics', year: 2023, finding: 'Healthcare workers have highest absenteeism rate at 4.3% of scheduled hours — more than any other sector', channel: 'Burnout: Absenteeism' },
    { author: 'Morrison et al.', year: 2003, finding: 'ICU noise averages 72 dB; peaks >85 dB from alarms; WHO recommends 35 dB for patient rooms', channel: 'Burnout: Absenteeism' },
    { author: 'Boubekri et al.', year: 2020, finding: 'Workers with optimized daylight show reduced stress and improved sleep quality, reducing sick days', channel: 'Burnout: Absenteeism' },
    { author: 'Bjornstad et al.', year: 2016, finding: 'Nature access reduces absenteeism by 6% through stress recovery and improved wellbeing', channel: 'Burnout: Absenteeism' },
  ],
  burnout_presenteeism: [
    { author: 'Hemp', year: 2004, finding: 'Presenteeism costs 3.5x absenteeism — working while impaired accounts for up to 89% of total burnout cost', channel: 'Burnout: Presenteeism' },
    { author: 'NSI Nursing Solutions', year: 2023, finding: 'Nurse burnout costs $16,736 per nurse; presenteeism is the largest hidden component', channel: 'Burnout: Presenteeism' },
    { author: 'Ryherd et al.', year: 2008, finding: 'Hospital noise levels 15-20 dB above WHO guidelines; contributes to cognitive fatigue and reduced performance', channel: 'Burnout: Presenteeism' },
    { author: 'Wilkins', year: 1989, finding: 'Visual pattern stress from fluorescent lighting and repetitive surfaces contributes to headaches and fatigue', channel: 'Burnout: Presenteeism' },
  ],
  clinical_accuracy: [
    { author: 'Boubekri et al.', year: 2020, finding: 'Workers with daylight access show +3% higher cognitive performance scores', channel: 'Clinical Accuracy' },
    { author: 'Allen et al.', year: 2015, finding: 'COGfx study: +8% cognitive function in green/well-ventilated buildings', channel: 'Clinical Accuracy' },
    { author: 'IOM / Bates et al.', year: 2023, finding: '1.5M preventable adverse drug events/yr in US; medication errors cost $5,000-$7,000 per event', channel: 'Clinical Accuracy' },
    { author: 'Nieuwenhuis et al.', year: 2014, finding: 'Enriched offices improve productivity by 6% vs lean offices; conservative 4% for clinical accuracy', channel: 'Clinical Accuracy' },
  ],
  efficiency: [
    { author: 'Hendrich et al.', year: 2008, finding: 'Nurses walk 4-5 miles per 12-hr shift; 50 min/shift on non-patient walking', channel: 'Efficiency' },
    { author: 'Shepley et al.', year: 2012, finding: 'Decentralized nursing stations reduce travel time 15-30 min/shift; improve response times', channel: 'Efficiency' },
    { author: 'Huelat', year: 2003, finding: 'Hospital wayfinding failures add 30% to visitor/patient anxiety and staff interruptions for directions', channel: 'Efficiency' },
    { author: 'Jamshidi et al.', year: 2025, finding: 'Better wayfinding recovers ~3% of FTE time in hospitals (vs 1% in offices) due to urgency and complexity', channel: 'Efficiency' },
  ],
  medication_errors: [
    { author: 'Bae & Fabry', year: 2014, finding: '82% of studies identify fatigue as contributing factor to medication errors; shift length and sleep quality are key mediators', channel: 'Medication Errors' },
    { author: 'Allen et al.', year: 2015, finding: 'COGfx: +8% cognitive function in well-ventilated buildings — directly affects medication calculation accuracy', channel: 'Medication Errors' },
    { author: 'Westbrook et al.', year: 2010, finding: 'Each interruption during medication administration increases clinical error risk by 12.7%; noise is primary source', channel: 'Medication Errors' },
    { author: 'IOM / Bates et al.', year: 2023, finding: '6.5 medication errors per 100 admissions; preventable ADEs cost $5,000-$7,000 each', channel: 'Medication Errors' },
  ],
  patient_falls: [
    { author: 'Joint Commission', year: 2015, finding: 'Review of 538 fatal/serious hospital falls: 39% identified the physical environment as part of the root cause', channel: 'Patient Falls' },
    { author: 'AHRQ / PSNet', year: 2023, finding: 'Falls are the most common adverse event in hospitals; ~3.5 per 100 admissions; each adds >$6,669 in costs', channel: 'Patient Falls' },
    { author: 'Hussain et al.', year: 2023, finding: 'Single-patient rooms may increase fall risk due to reduced surveillance; environmental design must compensate', channel: 'Patient Falls' },
    { author: 'Brandis', year: 1999, finding: 'Poor lighting, slippery surfaces, and inadequate handrails are consistent extrinsic fall risk factors in hospitals', channel: 'Patient Falls' },
  ],
  retention: [
    { author: 'NSI Nursing Solutions', year: 2024, finding: 'Average RN replacement cost $61,000; national RN turnover rate 16.4%', channel: 'Nurse Retention' },
    { author: 'Aiken et al.', year: 2002, finding: '41.5% of nurses who intend to leave cite burnout as root cause; environmental stressors are key contributors', channel: 'Nurse Retention' },
    { author: 'Bjornstad et al.', year: 2016, finding: 'Nature/greenery access reduces absenteeism by 6% and improves reported wellbeing, supporting retention', channel: 'Nurse Retention' },
    { author: 'AMN Healthcare', year: 2023, finding: 'Hospital nurse vacancy rate 9.9%; travel nurse costs 2-3x permanent staff', channel: 'Nurse Retention' },
  ],
  reputational_impact: [
    { author: 'CMS', year: 2024, finding: 'Value-Based Purchasing withholds 2% of Medicare revenue; HCAHPS is 25% of total VBP score', channel: 'Reputational Impact' },
    { author: 'Deloitte', year: 2016, finding: 'Top-quartile HCAHPS hospitals earn 4.7% net margin vs 1.8% for bottom quartile', channel: 'Reputational Impact' },
    { author: 'Ulrich et al.', year: 2008, finding: 'Post-renovation HCAHPS improved from 57% to 68% top-box (+11 points)', channel: 'Reputational Impact' },
    { author: 'Sadler et al.', year: 2011, finding: 'Private rooms increase HCAHPS quietness by 11.4 points (OR 1.78)', channel: 'Reputational Impact' },
    { author: 'Press Ganey', year: 2023, finding: 'Noise is the #1 lowest-scoring HCAHPS domain across US hospitals', channel: 'Reputational Impact' },
  ],
};

// ── Channel tooltip text (6 channels) ──
export const HC_STAFF_CHANNEL_TOOLTIPS = {
  'Burnout: Absenteeism': {
    formula: 'payroll \u00D7 4.3% baseline \u00D7 (noise 5% + daylight 4% + nature 3% + movement 2%)',
    pathways: 'Noise (5%) + Daylight (4%) + Nature (3%) + Movement (2%)',
    explanation: 'Healthcare workers have the highest absenteeism of any sector (4.3%, BLS). Noise control, circadian-appropriate lighting, nature access, and reduced walking distances each independently reduce stress-driven absence. Combined environmental improvements can recover significant lost staff-days per year.',
    citation: 'BLS 2023 (4.3% healthcare absenteeism); Morrison 2003; Boubekri 2020; Bjornstad 2016',
  },
  'Burnout: Presenteeism': {
    formula: 'payroll \u00D7 4.3% \u00D7 3.5 multiplier \u00D7 (noise 4% + daylight 3% + visual stress 2% + enrichment 2%)',
    pathways: 'Noise (4%) + Daylight (3%) + Visual Stress (2%) + Enrichment (2%)',
    explanation: 'Working while impaired accounts for up to 89% of burnout costs \u2014 the hidden majority. Nurses experiencing noise stress, circadian disruption, visual fatigue, and monotonous environments perform at reduced capacity, increasing error risk and extending task completion times.',
    citation: 'Hemp 2004 (3.5\u00D7 multiplier); NSI 2023 ($16,736/nurse); Ryherd 2008; Wilkins 1989',
  },
  'Clinical Accuracy': {
    formula: 'rework cost \u00D7 (daylight \u00D7 3% + air quality \u00D7 8% + enrichment \u00D7 4%)',
    pathways: 'Daylight (3%) + Air Quality (8%) + Enrichment (4%)',
    explanation: 'Cognitive performance directly impacts clinical decision-making, medication accuracy, and diagnostic precision. Better air quality shows the strongest effect (+8% cognitive function). With 1.5M preventable medication errors annually, even small accuracy improvements translate to significant avoided harm and cost.',
    citation: 'Allen 2015 COGfx; Boubekri 2020; Bates 2023 ($5-7K/med error)',
  },
  'Efficiency': {
    formula: 'key work cost \u00D7 (movement \u00D7 2% + wayfinding \u00D7 3%)',
    pathways: 'Movement (2%) + Wayfinding (3%)',
    explanation: 'Nurses walk 4-5 miles per 12-hour shift, with 50 minutes spent on non-patient-related walking. Better unit layout and wayfinding reduce wasted travel time, improve response times, and recover FTE capacity. Wayfinding is especially impactful in hospitals (3% vs 1% in offices) due to urgency and building complexity.',
    citation: 'Hendrich 2008; Shepley 2012; Huelat 2003',
  },
  'Medication Errors': {
    formula: 'admissions \u00D7 6.5% error rate \u00D7 $6,000/ADE \u00D7 15% env share \u00D7 (noise 6% + daylight 4% + air 8% + visual 2% + enrichment 3%)',
    pathways: 'Noise (6%) + Daylight (4%) + Air Quality (8%) + Visual Stress (2%) + Enrichment (3%)',
    explanation: '6.5 medication errors per 100 admissions; 82% of studies identify fatigue as a contributing factor. Environmental noise (interruptions), poor lighting (alertness), air quality (cognitive function), and visual stress (screen fatigue) directly affect the cognitive performance required for safe medication administration.',
    citation: 'Bae & Fabry 2014 (fatigue); Allen 2015 COGfx; Westbrook 2010 (interruptions); IOM/Bates 2023',
  },
  'Patient Falls': {
    formula: 'admissions \u00D7 3.5% fall rate \u00D7 $14,000/fall \u00D7 39% env share \u00D7 (daylight 6% + movement 5% + noise 3% + wayfinding 3%)',
    pathways: 'Daylight (6%) + Movement/Layout (5%) + Noise (3%) + Wayfinding (3%)',
    explanation: 'Falls are the most common hospital adverse event. The Joint Commission found 39% of serious falls have the physical environment as a root cause. Poor lighting (misjudging distances, not seeing obstacles), inadequate layout (long distances to bathroom, poor handrail placement), noise (sleep disruption weakening gait/balance), and confusing wayfinding (disoriented patients wandering) are all modifiable environmental risk factors.',
    citation: 'Joint Commission 2015 (39% env root cause); AHRQ/PSNet ($6,669+/fall); Hussain 2023; Brandis 1999',
  },
  'Nurse Retention': {
    formula: 'headcount \u00D7 turnover rate \u00D7 replacement cost \u00D7 (nature 6% + noise 3% + daylight 2%)',
    pathways: 'Nature (6%) + Noise (3%) + Daylight (2%)',
    explanation: 'RN replacement costs $61,000 per nurse with a 16.4% national turnover rate. 41.5% of nurses who intend to leave cite burnout as root cause. Nature access, noise control, and circadian-appropriate lighting reduce environmental burnout drivers, contributing to staff retention. In a market where travel nurses cost 2-3x permanent staff, reducing turnover through environmental quality has outsized financial impact.',
    citation: 'NSI 2024 ($61K/RN); Aiken 2002 (burnout\u2192turnover); Bjornstad 2016; AMN Healthcare 2023',
  },
  'Reputational Impact': {
    formula: 'VBP recovery (revenue \u00D7 2% \u00D7 25% \u00D7 composite) + Revenue uplift (revenue \u00D7 0.88%/star \u00D7 star gain), capped at 2% of revenue',
    pathways: 'Noise (8%) + Wayfinding (4%) + Daylight (3%) + Nature (3%) + Enrichment (2%)',
    explanation: 'Two mechanisms: (1) VBP reimbursement \u2014 CMS withholds 2% of Medicare revenue and redistributes based on HCAHPS scores (25% of VBP). (2) Revenue uplift \u2014 each HCAHPS star increase drives ~0.88% higher revenue through patient volume and payer mix. Environmental improvements can drive up to ~0.5 star gain (Ulrich 2008: +11 top-box points post-renovation). Top-quartile HCAHPS hospitals earn 4.7% net margin vs 1.8% for bottom quartile. Combined impact is capped at 2% of revenue as a conservative ceiling.',
    citation: 'CMS VBP (2% withhold, 25% HCAHPS weight); Deloitte 2016 (0.88%/star, 4.7% vs 1.8% margin); Ulrich 2008 (+11 pts post-renovation); Sadler 2011 (private rooms OR 1.78 quietness); Press Ganey 2023',
  },
};

// ── Baseline rates ──
const BASELINE_RATES = {
  reworkRate: 0.05,
  keyWorkFraction: 0.60,
  turnoverRate: 0.15,
};

// ── Helper: normalize pathway score to 0-1 improvement ──

function positiveImprovement(score) {
  // Positive pathways: raw score 0-1, higher = better
  return Math.max(0, score || 0);
}

function negativeImprovement(score) {
  // Negative pathways: raw score -1 to 0, closer to 0 = better
  // Improvement = how much better than worst case
  return Math.max(0, 1.0 - Math.abs(score || 0));
}

// ── Channel scoring functions ──

function burnoutAbsenteeismValue(pathwayScores, totalAnnualPayroll) {
  const noiseImp   = negativeImprovement(pathwayScores.noise) * 0.05;
  const daylightImp = positiveImprovement(pathwayScores.daylight) * 0.04;
  const natureImp  = positiveImprovement(pathwayScores.nature) * 0.03;
  const movementImp = positiveImprovement(pathwayScores.movement) * 0.02;

  const composite = noiseImp + daylightImp + natureImp + movementImp;
  const saving = totalAnnualPayroll * NURSE_BURNOUT_PARAMS.absenteeismRate * composite;
  const pct = totalAnnualPayroll > 0 ? saving / totalAnnualPayroll : 0;

  return { value: saving, pct };
}

function burnoutPresenteeismValue(pathwayScores, totalAnnualPayroll) {
  const noiseImp    = negativeImprovement(pathwayScores.noise) * 0.04;
  const daylightImp = positiveImprovement(pathwayScores.daylight) * 0.03;
  const visualImp   = negativeImprovement(pathwayScores.visual_stress) * 0.02;
  const enrichImp   = positiveImprovement(pathwayScores.enrichment) * 0.02;

  const composite = noiseImp + daylightImp + visualImp + enrichImp;
  const baseCost = totalAnnualPayroll * NURSE_BURNOUT_PARAMS.absenteeismRate * NURSE_BURNOUT_PARAMS.presenteeismMultiplier;
  const saving = baseCost * composite;
  const pct = totalAnnualPayroll > 0 ? saving / totalAnnualPayroll : 0;

  return { value: saving, pct };
}

function clinicalAccuracyChannelValue(pathwayScores, totalReworkCost) {
  const daylight = Math.max(0, pathwayScores.daylight || 0);
  const air = Math.max(0, 1.0 + (pathwayScores.air_quality || 0));
  const enrichment = Math.max(0, pathwayScores.enrichment || 0);

  const saving = (
    daylight * HC_PATHWAY_COEFFICIENTS.daylight.effect +
    air * HC_PATHWAY_COEFFICIENTS.air_quality.effect +
    enrichment * HC_PATHWAY_COEFFICIENTS.enrichment.effect
  ) * totalReworkCost;
  const pct = totalReworkCost > 0 ? saving / totalReworkCost : 0;

  return { value: saving, pct };
}

function efficiencyChannelValue(pathwayScores, totalKeyWorkCost) {
  const movement = Math.max(0, pathwayScores.movement || 0);
  const wayfinding = Math.max(0, pathwayScores.wayfinding || 0);

  const saving = (
    movement * HC_PATHWAY_COEFFICIENTS.movement.effect +
    wayfinding * HC_PATHWAY_COEFFICIENTS.wayfinding.effect
  ) * totalKeyWorkCost;
  const pct = totalKeyWorkCost > 0 ? saving / totalKeyWorkCost : 0;

  return { value: saving, pct };
}

function medicationErrorsValue(pathwayScores, annualAdmissions) {
  if (!annualAdmissions || annualAdmissions <= 0) return { value: 0, pct: 0 };

  const noiseImp    = negativeImprovement(pathwayScores.noise) * 0.06;
  const daylightImp = positiveImprovement(pathwayScores.daylight) * 0.04;
  const airImp      = negativeImprovement(pathwayScores.air_quality) * 0.08;
  const visualImp   = negativeImprovement(pathwayScores.visual_stress) * 0.02;
  const enrichImp   = positiveImprovement(pathwayScores.enrichment) * 0.03;

  const composite = noiseImp + daylightImp + airImp + visualImp + enrichImp;
  const totalErrorCost = annualAdmissions * MED_ERROR_PARAMS.baseRatePerAdmission * MED_ERROR_PARAMS.costPerPreventableADE;
  const saving = totalErrorCost * MED_ERROR_PARAMS.environmentalSharePct * composite;

  return { value: saving, pct: composite };
}

function patientFallsValue(pathwayScores, annualAdmissions) {
  if (!annualAdmissions || annualAdmissions <= 0) return { value: 0, pct: 0 };

  const daylightImp  = positiveImprovement(pathwayScores.daylight) * 0.06;
  const movementImp  = positiveImprovement(pathwayScores.movement) * 0.05;
  const noiseImp     = negativeImprovement(pathwayScores.noise) * 0.03;
  const wayfindingImp = positiveImprovement(pathwayScores.wayfinding) * 0.03;

  const composite = daylightImp + movementImp + noiseImp + wayfindingImp;
  const totalFallCost = annualAdmissions * PATIENT_FALL_PARAMS.fallRatePerAdmission * PATIENT_FALL_PARAMS.costPerFall;
  const saving = totalFallCost * PATIENT_FALL_PARAMS.environmentalSharePct * composite;

  return { value: saving, pct: composite };
}

function nurseRetentionValue(pathwayScores, annualTurnoverCost) {
  const natureImp   = positiveImprovement(pathwayScores.nature) * 0.06;
  const noiseImp    = negativeImprovement(pathwayScores.noise) * 0.03;
  const daylightImp = positiveImprovement(pathwayScores.daylight) * 0.02;

  const composite = natureImp + noiseImp + daylightImp;
  const saving = composite * annualTurnoverCost;
  const pct = annualTurnoverCost > 0 ? saving / annualTurnoverCost : 0;

  return { value: saving, pct };
}

function reputationalImpactValue(pathwayScores, annualRevenue) {
  if (!annualRevenue || annualRevenue <= 0) return { value: 0, pct: 0 };

  // Pathway contributions weighted by HCAHPS domain relevance
  const noiseImp      = negativeImprovement(pathwayScores.noise) * 0.08;
  const wayfindingImp = positiveImprovement(pathwayScores.wayfinding) * 0.04;
  const daylightImp   = positiveImprovement(pathwayScores.daylight) * 0.03;
  const natureImp     = positiveImprovement(pathwayScores.nature) * 0.03;
  const enrichImp     = positiveImprovement(pathwayScores.enrichment) * 0.02;

  const composite = noiseImp + wayfindingImp + daylightImp + natureImp + enrichImp;
  // composite range: 0–0.20 (sum of weights)

  // 1. VBP reimbursement recovery: get back portion of 2% Medicare withhold
  const vbpPool = annualRevenue * REPUTATIONAL_PARAMS.vbpWithholdPct;
  const vbpGain = vbpPool * REPUTATIONAL_PARAMS.hcahpsWeightInVbp * composite;

  // 2. Revenue uplift: each HCAHPS star ≈ 0.88% revenue increase (Deloitte/CMS)
  //    Environment can drive up to ~0.5 star gain; scale by composite / max composite
  const maxComposite = 0.20;
  const starGain = REPUTATIONAL_PARAMS.maxStarGain * Math.min(composite / maxComposite, 1.0);
  const revenueUplift = annualRevenue * REPUTATIONAL_PARAMS.revenuePerHcahpsPoint * starGain;

  // Combined, capped at 2% of total revenue as conservative ceiling
  const cap = annualRevenue * REPUTATIONAL_PARAMS.maxHcahpsGainPct;
  const saving = Math.min(vbpGain + revenueUplift, cap);

  return { value: saving, pct: composite };
}

/**
 * Calculate hospital staff financial ROI.
 *
 * @param {number} brainHealthScore - Overall brain health score (0-1)
 * @param {object} pathwayScores - Per-pathway scores from assessment
 * @param {Array|object} peopleData - Staff role data (headcount, hourly cost, etc.)
 * @param {number} projectCost - Total project/facility cost for ROI calculation
 * @param {object} options - Additional options
 * @param {number} options.annualAdmissions - Annual patient admissions for med error channel
 * @param {number} options.annualRevenue - Annual Medicare revenue for reputational impact channel
 */
export function calculateHcStaffRoi(brainHealthScore, pathwayScores, peopleData, projectCost = 0, options = {}) {
  const people = Array.isArray(peopleData) ? peopleData : [peopleData];
  const annualAdmissions = Number(options.annualAdmissions) || 0;
  const annualRevenue = Number(options.annualRevenue) || 0;

  let totalAnnualPayroll = 0;
  let totalHeadcount = 0;
  let totalReplacementPool = 0;
  let totalReworkHoursCost = 0;
  let totalKeyWorkCost = 0;
  let weightedTurnoverRate = 0;

  for (const person of people) {
    const hc = Number(person.headcount) || 0;
    const hourly = Number(person.avgHourlyCost || person.avg_hourly_cost) || 0;
    const hoursPerWeek = Number(person.hoursPerWeek || person.hours_per_week) || 40;
    const annualHours = hoursPerWeek * 47;
    const replMult = Number(person.replacementCostMultiple || person.avg_replacement_cost_pct) || 1.5;
    const reworkPct = Number(person.pctTimeRework || person.pct_time_rework) || BASELINE_RATES.reworkRate;
    const keyPct = Number(person.pctTimeKeyWork || person.pct_time_key_work) || BASELINE_RATES.keyWorkFraction;
    const turnover = Number(person.annualTurnoverRate || person.annual_turnover_rate) || BASELINE_RATES.turnoverRate;

    const annual = hc * hourly * annualHours;
    totalAnnualPayroll += annual;
    totalHeadcount += hc;
    totalReplacementPool += hc * hourly * annualHours * replMult;
    totalReworkHoursCost += annual * reworkPct;
    totalKeyWorkCost += annual * keyPct;
    weightedTurnoverRate += turnover * hc;
  }

  const emptyResult = {
    overallBrainHealthScore: brainHealthScore,
    channels: [
      { name: 'Burnout: Absenteeism', annualSavings: 0, pct: 0 },
      { name: 'Burnout: Presenteeism', annualSavings: 0, pct: 0 },
      { name: 'Clinical Accuracy', annualSavings: 0, pct: 0 },
      { name: 'Efficiency', annualSavings: 0, pct: 0 },
      { name: 'Medication Errors', annualSavings: 0, pct: 0 },
      { name: 'Patient Falls', annualSavings: 0, pct: 0 },
      { name: 'Nurse Retention', annualSavings: 0, pct: 0 },
      { name: 'Reputational Impact', annualSavings: 0, pct: 0 },
    ],
    totalAnnualImpact: 0,
    totalAnnualBenefit: 0,
    totalAnnualSavings: 0,
    paybackPct: 0,
    paybackYears: null,
    roi: null,
    // Summary fields for dashboard cards
    burnoutSummary: { total: 0, daysAvoided: 0, riskLevel: 'unknown' },
    medErrorSummary: { total: 0, errorsAvoided: 0, topPathway: null },
    fallsSummary: { total: 0, fallsAvoided: 0 },
  };

  if (totalHeadcount === 0) return emptyResult;

  const avgTurnover = weightedTurnoverRate / totalHeadcount;
  const avgReplacement = totalReplacementPool / totalHeadcount;
  const annualTurnoverCost = totalHeadcount * avgTurnover * avgReplacement;

  // Compute all 8 channels
  const absenteeism = burnoutAbsenteeismValue(pathwayScores, totalAnnualPayroll);
  const presenteeism = burnoutPresenteeismValue(pathwayScores, totalAnnualPayroll);
  const accuracy = clinicalAccuracyChannelValue(pathwayScores, totalReworkHoursCost);
  const efficiency = efficiencyChannelValue(pathwayScores, totalKeyWorkCost);
  const medErrors = medicationErrorsValue(pathwayScores, annualAdmissions);
  const falls = patientFallsValue(pathwayScores, annualAdmissions);
  const retention = nurseRetentionValue(pathwayScores, annualTurnoverCost);
  const reputational = reputationalImpactValue(pathwayScores, annualRevenue);

  const totalAnnual = absenteeism.value + presenteeism.value + accuracy.value +
                      efficiency.value + medErrors.value + falls.value + retention.value +
                      reputational.value;
  const paybackPct = projectCost > 0 ? (totalAnnual / projectCost * 100) : 0;
  const paybackYears = totalAnnual > 0 ? (projectCost / totalAnnual) : null;
  const roi = projectCost > 0 ? totalAnnual / projectCost : null;

  const round2 = v => Math.round(v * 100) / 100;
  const round4 = v => Math.round(v * 10000) / 10000;

  const channels = [
    { name: 'Burnout: Absenteeism', annualSavings: round2(absenteeism.value), pct: round4(absenteeism.pct) },
    { name: 'Burnout: Presenteeism', annualSavings: round2(presenteeism.value), pct: round4(presenteeism.pct) },
    { name: 'Clinical Accuracy', annualSavings: round2(accuracy.value), pct: round4(accuracy.pct) },
    { name: 'Efficiency', annualSavings: round2(efficiency.value), pct: round4(efficiency.pct) },
    { name: 'Medication Errors', annualSavings: round2(medErrors.value), pct: round4(medErrors.pct) },
    { name: 'Patient Falls', annualSavings: round2(falls.value), pct: round4(falls.pct) },
    { name: 'Nurse Retention', annualSavings: round2(retention.value), pct: round4(retention.pct) },
    { name: 'Reputational Impact', annualSavings: round2(reputational.value), pct: round4(reputational.pct) },
  ];

  // ── Burnout summary for dashboard card ──
  const burnoutTotal = absenteeism.value + presenteeism.value;
  // Estimate average hourly cost for days calculation
  const avgHourly = totalHeadcount > 0 ? totalAnnualPayroll / (totalHeadcount * 40 * 47) : 45;
  const daysAvoided = avgHourly > 0 ? Math.round(absenteeism.value / (avgHourly * 8)) : 0;

  // Risk level based on noise + daylight + nature pathway performance
  const noiseScore = 1.0 - Math.abs(pathwayScores.noise || 0);
  const daylightScore = pathwayScores.daylight || 0;
  const natureScore = pathwayScores.nature || 0;
  const envAvg = (noiseScore + daylightScore + natureScore) / 3;
  let riskLevel = 'high';
  if (envAvg >= 0.6) riskLevel = 'low';
  else if (envAvg >= 0.35) riskLevel = 'moderate';

  // ── Med error summary for dashboard card ──
  const errorsAvoided = MED_ERROR_PARAMS.costPerPreventableADE > 0
    ? Math.round(medErrors.value / MED_ERROR_PARAMS.costPerPreventableADE)
    : 0;

  // Find top contributing pathway (which has most room for improvement)
  const medPathways = [
    { name: 'Noise', score: noiseScore, weight: 0.06 },
    { name: 'Daylight', score: daylightScore, weight: 0.04 },
    { name: 'Air Quality', score: 1.0 - Math.abs(pathwayScores.air_quality || 0), weight: 0.08 },
    { name: 'Visual Comfort', score: 1.0 - Math.abs(pathwayScores.visual_stress || 0), weight: 0.02 },
    { name: 'Enrichment', score: pathwayScores.enrichment || 0, weight: 0.03 },
  ];
  // Top contributor = pathway with largest (1 - score) * weight (most room for weighted improvement)
  let topPathway = null;
  let topGap = 0;
  for (const p of medPathways) {
    const gap = (1.0 - p.score) * p.weight;
    if (gap > topGap) {
      topGap = gap;
      topPathway = p.name;
    }
  }

  return {
    overallBrainHealthScore: round4(brainHealthScore),
    channels,
    totalAnnualImpact: round2(totalAnnual),
    totalAnnualBenefit: round2(totalAnnual),
    totalAnnualSavings: round2(totalAnnual),
    paybackPct: round2(paybackPct),
    paybackYears: paybackYears !== null ? round2(paybackYears) : null,
    roi: roi !== null ? round4(roi) : null,
    // Summary fields for dashboard cards
    burnoutSummary: {
      total: round2(burnoutTotal),
      daysAvoided,
      riskLevel,
    },
    medErrorSummary: {
      total: round2(medErrors.value),
      errorsAvoided,
      topPathway,
    },
    fallsSummary: {
      total: round2(falls.value),
      fallsAvoided: PATIENT_FALL_PARAMS.costPerFall > 0
        ? Math.round(falls.value / PATIENT_FALL_PARAMS.costPerFall)
        : 0,
    },
    reputationalSummary: {
      total: round2(reputational.value),
      hcahpsPointsGained: round2(reputational.pct * 100),
      topPathway: (() => {
        const rpw = [
          { name: 'Noise', val: negativeImprovement(pathwayScores.noise) * 0.08 },
          { name: 'Wayfinding', val: positiveImprovement(pathwayScores.wayfinding) * 0.04 },
          { name: 'Daylight', val: positiveImprovement(pathwayScores.daylight) * 0.03 },
          { name: 'Nature', val: positiveImprovement(pathwayScores.nature) * 0.03 },
          { name: 'Enrichment', val: positiveImprovement(pathwayScores.enrichment) * 0.02 },
        ];
        return rpw.reduce((a, b) => a.val >= b.val ? a : b).name;
      })(),
    },
  };
}
